import { useRef } from "react";

import { Box, List, Paper, Typography } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { useInfiniteScrollSentinel } from "hooks";

import { CarrierSearchResultRow } from "./CarrierSearchResultRow";
import { CarrierSearchResultRowsSkeleton } from "./CarrierSearchResultRowsSkeleton";
import {
  CARRIER_SEARCH_DROPDOWN_MAX_HEIGHT,
  CARRIER_SEARCH_FIRST_PAGE_SKELETON_ROWS,
  CARRIER_SEARCH_LOAD_MORE_SKELETON_ROWS,
} from "../../constants";
import { buildCarrierSearchOptionId } from "../../utils";

import type { CarrierCompanySearchState } from "../../hooks/useCarrierCompanySearch";
import type { CarrierSearchResult } from "../../types";

// Enough of a target for the observer to catch as the last row scrolls past,
// without holding open a gap under a list that has nothing more to load.
const SENTINEL_HEIGHT = 8;

interface Props {
  searchValue: string;
  search: CarrierCompanySearchState;
  highlightedIndex: number;
  listboxId: string;
  onSelect: (result: CarrierSearchResult) => void;
}

export function CarrierSearchResultsDropdown(props: Props) {
  const { searchValue, search, highlightedIndex, listboxId, onSelect } = props;
  const {
    results,
    isLoadingFirstPage,
    isLoadingMorePages,
    isEmptyResult,
    hasMore,
    loadMore,
    error,
  } = search;

  const sentinelRef = useRef<HTMLDivElement>(null);

  useInfiniteScrollSentinel({
    sentinelRef,
    onReachEnd: loadMore,
    enabled: hasMore && !isLoadingMorePages,
  });

  const renderContent = () => {
    if (error != null) {
      return (
        <Box sx={{ p: 2 }}>
          <ErrorTypography error={error} />
        </Box>
      );
    }

    if (isLoadingFirstPage) {
      return (
        <CarrierSearchResultRowsSkeleton
          rowCount={CARRIER_SEARCH_FIRST_PAGE_SKELETON_ROWS}
          label="Loading carriers"
        />
      );
    }

    if (isEmptyResult) {
      return (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body2">
            No carriers found for &quot;{searchValue}&quot;
          </Typography>
        </Box>
      );
    }

    if (results.length === 0) {
      return null;
    }

    return (
      <>
        <List disablePadding id={listboxId} role="listbox">
          {results.map((result, index) => (
            <CarrierSearchResultRow
              key={result.company.id}
              id={buildCarrierSearchOptionId(listboxId, index)}
              result={result}
              isHighlighted={index === highlightedIndex}
              onSelect={onSelect}
            />
          ))}
        </List>
        {/*
        Sits below the list so the listbox holds only options, and because a div
        is not valid inside a ul. The placeholder rows follow the sentinel: the
        observer has already fired by the time they are drawn, so keeping them
        out of the sentinel leaves its height fixed.
        */}
        <Box ref={sentinelRef} sx={{ height: hasMore ? SENTINEL_HEIGHT : 0 }} />
        {isLoadingMorePages && (
          <CarrierSearchResultRowsSkeleton
            rowCount={CARRIER_SEARCH_LOAD_MORE_SKELETON_ROWS}
            label="Loading more carriers"
          />
        )}
      </>
    );
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: "absolute",
        top: "100%",
        right: { xs: 16, sm: 0 },
        left: { xs: 16, sm: 0 },
        width: { xs: "auto", sm: "100%" },
        maxWidth: "90vw",
        mt: 1,
        maxHeight: CARRIER_SEARCH_DROPDOWN_MAX_HEIGHT,
        overflowY: "auto",
        borderRadius: 1,
        color: "text.primary",
      }}
    >
      {renderContent()}
    </Paper>
  );
}
